/**
 * レアリティが決まらない登録済みの画像（Issue #950）: 型番はあるがレアリティが空のもの。
 * 型番が収録パックに無い（打ち間違い？）ものは型番を直す（直して 1 つに決まればサーバーがレアリティも入れる）、
 * 型番に複数のレアリティがあるものはレアリティを選ぶ。レアリティが入ったら一覧から消える。
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/api';
import CardRegionZoom, { CARD_REGIONS } from './CardRegionZoom';
import ZoomableImage from './ZoomableImage';
import RarityField, { raritiesFor } from './RarityField';

const PAGE = 50;
const REASON_LABEL = { no_candidate: '型番が収録パックに無い', ambiguous: 'レアリティが複数' };

function RarityRow({ row, onSaved, onChanged }) {
  const [code, setCode] = useState(row.set_code ?? '');
  const [options, setOptions] = useState(null);
  const [rarity, setRarity] = useState('');
  const [busy, setBusy] = useState(false);
  const listId = `unresolved-rarity-codes-${row.id}`;
  const codeChanged = code.trim().toUpperCase() !== String(row.set_code ?? '').toUpperCase();
  const rarityOptions = codeChanged ? raritiesFor(options, code) : row.rarities;

  const loadOptions = async () => {
    if (options) return;
    try {
      const codes = await api.project('GetCardSetCodes', { card_id: String(row.card_id) });
      setOptions(Array.isArray(codes) ? codes : []);
    } catch { setOptions([]); }
  };
  const save = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      let image = null;
      if (codeChanged) image = await api.project('SetCardImageSetCode', { card_image_id: row.id, set_code: code });
      if (rarity.trim()) image = await api.project('SetCardImageRarity', { card_image_id: row.id, rarity });
      if (image?.rarity) onSaved(row.id);
      else if (image) onChanged(row.id, image);
    } catch (err) {
      alert('保存できませんでした: ' + err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" data-testid={`unresolved-rarity-${row.id}`}
      style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <ZoomableImage src={row.download_url} alt={row.set_code} testId={`unresolved-rarity-image-${row.id}`} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
        <div className="card-meta">
          <strong>{row.card_name ?? '不明'}</strong>（{row.card_id}・{row.language_code} #{String(row.image_index).padStart(2, '0')}）
          — {REASON_LABEL[row.reason] ?? row.reason}
          {row.reason === 'ambiguous' && `（${row.rarities.join(' / ')}）`}
        </div>
        <form style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}
          onSubmit={(e) => { e.preventDefault(); save(); }}>
          <CardRegionZoom src={row.download_url} region={CARD_REGIONS.setCode} width={280} label="型番" />
          <input className="form-input" style={{ width: 170 }} placeholder="型番（例 SD01-JP001）" list={listId}
            data-testid={`unresolved-rarity-code-${row.id}`}
            value={code} disabled={busy} onFocus={loadOptions} onChange={(e) => setCode(e.target.value)} />
          <datalist id={listId}>
            {(options ?? []).map((c) => <option key={c.set_code} value={c.set_code}>{c.pack_name ?? ''}</option>)}
          </datalist>
          <RarityField id={`unresolved-rarity-list-${row.id}`} testId={`unresolved-rarity-value-${row.id}`}
            value={rarity} disabled={busy} options={rarityOptions} onChange={setRarity} />
          <button type="submit" className="btn btn-primary btn-sm"
            disabled={busy || !code.trim() || (!codeChanged && !rarity.trim())}>保存</button>
        </form>
      </div>
    </div>
  );
}

export default function UnresolvedRarities({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [shown, setShown] = useState(PAGE);
  const [reason, setReason] = useState('all');

  const load = useCallback(() => api.project('GetUnresolvedRarities')
    .then((r) => setRows(Array.isArray(r) ? r : []))
    .catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const drop = (id) => setRows((prev) => prev.filter((r) => r.id !== id));
  // 型番を直したがレアリティが決まらなかった: 新しい型番で候補を引き直す
  const changed = () => load();
  const count = (k) => rows.filter((r) => r.reason === k).length;
  const visible = reason === 'all' ? rows : rows.filter((r) => r.reason === reason);

  return (
    <div style={{ marginTop: 32 }}>
      <h2 style={{ fontSize: '1rem', marginBottom: 4, color: '#aaa' }}>
        レアリティが決まらない登録済みの画像 ({rows.length}件)
      </h2>
      <div className="card-meta" style={{ marginBottom: 12 }}>
        型番から 1 つに決まらなかったものです。型番が収録パックに無いものは型番を直し、レアリティが複数あるものは選んで保存してください。
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {[['all', `すべて (${rows.length})`], ['no_candidate', `型番が収録パックに無い (${count('no_candidate')})`],
          ['ambiguous', `レアリティが複数 (${count('ambiguous')})`]].map(([k, label]) => (
          <button key={k} data-testid={`unresolved-rarity-filter-${k}`}
            className={`btn btn-sm ${reason === k ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setReason(k); setShown(PAGE); }}>{label}</button>
        ))}
      </div>
      {visible.length === 0 && <p className="loading">ありません</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {visible.slice(0, shown).map((row) => (
          <RarityRow key={`${row.id}-${row.set_code}`} row={row} onSaved={drop} onChanged={changed} />
        ))}
      </div>
      {visible.length > shown && (
        <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={() => setShown((n) => n + PAGE)}>
          もっと見る（残り {visible.length - shown} 件）
        </button>
      )}
    </div>
  );
}
